import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Filters,
  Modal,
  ModalActions,
  MultiSelect,
  Page,
  PageHeader,
  Table,
  Toolbar,
  type Action,
  type Column,
  type FilterValues,
} from '@evogard/ui';
import { formatPercentage, loadFailureMessage } from '@evogard/domain';
import type { AwardTemplate } from '@evogard/contract';
import { MoneyValue } from '../../components/Values.js';
import { api } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';
import { AwardForm, familyLabel } from './ConsultantAwards.js';
import { refusalOf, useConsultants } from './common.js';

const TEMPLATES_KEY = ['sales', 'award-templates'] as const;

/**
 * Os MODELOS de premiação — a régua cadastrada uma vez, e vinculada a quantos
 * consultores for preciso. Pedido do responsável em 23/09/2026.
 *
 * ## Vincular é criar, e o que nasce é de cada um
 *
 * Vincular cria uma premiação INDEPENDENTE para cada consultor escolhido, na
 * ficha dele. Editar o modelo vale para as vinculações seguintes e para quem
 * for MARCADO no formulário — desde 15/10/2026 ele pergunta, um por um, a
 * quais vinculados a régua nova se aplica, e nenhum vem marcado. Editar a
 * premiação de um consultor não mexe no modelo nem nas dos outros.
 *
 * ## Desvincular arquiva; apagar some de vez
 *
 * Os dois atos ficam lado a lado. Apagar pede o segundo clique e só passa na
 * premiação que nunca gerou extrato — o servidor recusa a outra, e a frase
 * manda arquivar.
 *
 * ## Quem está com cada modelo
 *
 * A lista mostra quem tem hoje uma premiação ATIVA nascida do modelo. Arquivar
 * a premiação de alguém tira a pessoa da lista, e é também o que libera
 * vinculá-la de novo — o servidor recusa a segunda cópia ativa na mesma
 * pessoa, que pagaria duas vezes.
 */
export function AwardTemplatesScreen() {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const canEdit = can(context, 'sales.commissions', 'configurar_premiacao');
  const client = useQueryClient();

  const [editing, setEditing] = useState<AwardTemplate | 'novo' | null>(null);
  const [linking, setLinking] = useState<AwardTemplate | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /*
   * A busca e o recorte valem para as DUAS tabelas — por isso moram numa
   * `Toolbar` acima delas, e não na barra de uma (ver REFERENCIA.md, "Recorte").
   * A busca filtra enquanto se digita; o resto só vale no "Filtrar".
   */
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<FilterValues>(NO_FILTER);
  /*
   * Os arquivados ficam fora por padrão. Até 14/10/2026 a tela nunca os pedia:
   * arquivar um modelo o tirava da vista para sempre, e não havia como
   * desarquivar. Desde 15/10 é o filtro "Situação", e não mais um botão.
   */
  const situation = String(filters.situation || 'ativos') as Situation;
  const showArchived = situation !== 'ativos';

  const templates = useQuery({
    queryKey: [...TEMPLATES_KEY, tenantId, showArchived],
    enabled: canEdit,
    queryFn: async () => {
      const r = await api.sales.listAwardTemplates({
        query: showArchived ? { includeArchived: 'true' } : {},
      });
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'os modelos'));
      return r.body;
    },
  });

  /* Os tipos do espelho, para a premiação direta marcar códigos — ver `ConsultantAwards`. */
  const vehicleFilters = useQuery({
    queryKey: ['erp', 'vehicle-filters', tenantId],
    enabled: canEdit,
    queryFn: async () => {
      const r = await api.erp.vehicleFilters();
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'os tipos do cadastro do ERP'));
      return r.body;
    },
  });

  const archive = useMutation({
    mutationFn: async (template: AwardTemplate) => {
      const r = await api.sales.editAwardTemplate({
        params: { id: template.id },
        body: { action: 'arquivar', archived: template.archivedAt === null },
      });
      if (r.status !== 200) throw new Error(refusalOf(r, 'o modelo'));
    },
    onSuccess: () => {
      setFailure(null);
      void client.invalidateQueries({ queryKey: TEMPLATES_KEY });
    },
    onError: (e: Error) => setFailure(e.message),
  });

  /*
   * Desvincular é ARQUIVAR a premiação que a pessoa ganhou do modelo — o mesmo
   * ato do "Arquivar" da ficha dela, e é lá que ele se desfaz. Não pede
   * confirmação porque não apaga nada.
   */
  const unlink = useMutation({
    mutationFn: async (linked: AwardTemplate['consultants'][number]) => {
      const r = await api.sales.editAward({
        params: { id: linked.awardId },
        body: { action: 'arquivar', archived: true },
      });
      if (r.status !== 200) throw new Error(refusalOf(r, 'a premiação'));
      return linked;
    },
    onSuccess: (linked) => {
      setFailure(null);
      setNotice(
        `Desvinculado: a premiação de ${linked.user.nome} foi arquivada, e pode ser desarquivada na ficha, em Consultores e times.`,
      );
      void client.invalidateQueries({ queryKey: TEMPLATES_KEY });
      void client.invalidateQueries({ queryKey: ['sales', 'awards'] });
      void client.invalidateQueries({ queryKey: ['sales', 'management'] });
    },
    onError: (e: Error) => setFailure(e.message),
  });

  /*
   * Apagar é o outro ato, e não substitui o de cima (15/10/2026): some de vez,
   * e só para a premiação que nunca gerou extrato — o servidor recusa a que
   * já pagou alguma coisa, e a frase dele diz para arquivar.
   */
  const remove = useMutation({
    mutationFn: async (linked: AwardTemplate['consultants'][number]) => {
      const r = await api.sales.deleteAward({
        params: { id: linked.awardId },
        body: undefined,
      });
      if (r.status !== 204) throw new Error(refusalOf(r, 'a premiação'));
      return linked;
    },
    onSuccess: (linked) => {
      setFailure(null);
      setNotice(
        `Apagada: a premiação de ${linked.user.nome} deste modelo não existe mais.`,
      );
      void client.invalidateQueries({ queryKey: TEMPLATES_KEY });
      void client.invalidateQueries({ queryKey: ['sales', 'awards'] });
      void client.invalidateQueries({ queryKey: ['sales', 'management'] });
    },
    onError: (e: Error) => setFailure(e.message),
  });

  if (!canEdit) {
    return (
      <Page measure="cheia">
        <PageHeader title="Modelos de premiação" />
        <Alert tone="informacao">
          Os modelos são cadastro de premiação, e pedem a ação{' '}
          <strong className="font-medium">configurar premiação</strong> em Comissões.
        </Alert>
      </Page>
    );
  }

  const busy = editing !== null || linking !== null;
  const unlinking = unlink.isPending
    ? (unlink.variables?.awardId ?? null)
    : remove.isPending
      ? (remove.variables?.awardId ?? null)
      : null;

  /* A busca e o recorte, no navegador: a lista inteira já está na mão. */
  const shown = (templates.data ?? []).filter((t) =>
    matches(t, search, filters, situation),
  );

  const columns: Column<AwardTemplate>[] = [
    {
      title: 'Modelo',
      alignment: 'inicio',
      minWidth: '14rem',
      sortValue: (t) => t.title.toLocaleLowerCase('pt-BR'),
      cell: (t) => (
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-texto">{t.title}</span>
          {t.archivedAt !== null && <Badge tone="alerta">arquivado</Badge>}
          {t.basisSource === 'hierarquia' && <Badge tone="informacao">de líder</Badge>}
        </span>
      ),
    },
    {
      title: 'Tipo',
      sortValue: (t) => familyLabel(t.family),
      cell: (t) => <Badge tone="neutro">{familyLabel(t.family)}</Badge>,
    },
    {
      title: 'Régua',
      alignment: 'inicio',
      minWidth: '16rem',
      cell: (t) => <span className="text-sm text-texto">{ruleOf(t)}</span>,
    },
    {
      title: 'Vinculados',
      alignment: 'fim',
      sortValue: (t) => t.consultants.length,
      cell: (t) => t.consultants.length,
    },
  ];

  const actionsOf = (t: AwardTemplate): Action[] => {
    const wait: string | false = busy ? 'Feche o formulário aberto antes.' : false;
    return [
      /* Modelo arquivado não vincula: desarquive primeiro. */
      ...(t.archivedAt === null
        ? [
            {
              label: 'Vincular a consultores',
              disabled: wait,
              run: () => {
                setNotice(null);
                setLinking(t);
              },
            },
          ]
        : []),
      {
        label: 'Editar',
        disabled: wait,
        run: () => {
          setNotice(null);
          setEditing(t);
        },
      },
      {
        label: t.archivedAt === null ? 'Arquivar' : 'Desarquivar',
        disabled: wait,
        run: () => archive.mutate(t),
      },
    ];
  };

  return (
    <Page measure="cheia">
      <PageHeader
        title="Modelos de premiação"
        actions={
          <Button
            onClick={() => {
              setNotice(null);
              setEditing('novo');
            }}
          >
            Novo modelo
          </Button>
        }
      >
        Cadastre a régua uma vez e vincule aos consultores. Cada vínculo cria uma
        premiação na ficha da pessoa, que pode ser editada só para ela — e, ao editar o
        modelo, você escolhe a quais vinculados a mudança se aplica.
      </PageHeader>

      {failure !== null && <Alert tone="erro">{failure}</Alert>}
      {notice !== null && <Alert tone="sucesso">{notice}</Alert>}
      {templates.isError && <Alert tone="erro">{templates.error.message}</Alert>}

      {/*
       * Os dois atos abrem em MODAL desde 15/10/2026. Inline, eles nasciam no
       * topo da página, acima da lista — e quem clicava "Editar" num modelo do
       * fim da lista não via nada acontecer até rolar de volta.
       */}
      {editing !== null && (
        <AwardForm
          frame="modal"
          award={editing === 'novo' ? null : editing}
          consultant={null}
          filters={vehicleFilters.data ?? null}
          filtersFailure={vehicleFilters.isError ? vehicleFilters.error.message : null}
          onClose={() => setEditing(null)}
          onSaved={(applied) => {
            const linked = editing === 'novo' ? 0 : editing.consultants.length;
            setNotice(
              editing === 'novo'
                ? 'Modelo cadastrado. Vincule-o aos consultores pela lista.'
                : applied === 0
                  ? 'Modelo salvo. Quem já estava vinculado continua com a régua de antes.'
                  : applied === linked
                    ? 'Modelo salvo, e aplicado a todos os vinculados.'
                    : `Modelo salvo, e aplicado a ${applied} de ${linked} vinculado(s). Os outros continuam com a régua de antes.`,
            );
            setEditing(null);
            setFailure(null);
            void client.invalidateQueries({ queryKey: TEMPLATES_KEY });
            if (applied > 0) {
              void client.invalidateQueries({ queryKey: ['sales', 'awards'] });
              void client.invalidateQueries({ queryKey: ['sales', 'management'] });
            }
          }}
          onFailure={setFailure}
        />
      )}

      {linking !== null && (
        <LinkPanel
          template={linking}
          onClose={() => setLinking(null)}
          onLinked={(count) => {
            setNotice(
              count === 1
                ? '1 premiação criada, na ficha do consultor.'
                : `${count} premiações criadas, uma na ficha de cada consultor.`,
            );
            setLinking(null);
            setFailure(null);
            void client.invalidateQueries({ queryKey: TEMPLATES_KEY });
            void client.invalidateQueries({ queryKey: ['sales', 'awards'] });
            void client.invalidateQueries({ queryKey: ['sales', 'management'] });
          }}
        />
      )}

      <Toolbar
        search={{
          value: search,
          onChange: setSearch,
          label: 'Buscar modelo',
          placeholder: 'Buscar pelo nome do modelo ou de um vinculado…',
        }}
        toolbar={
          <Filters
            values={filters}
            onApply={setFilters}
            fields={[
              {
                type: 'selecao',
                name: 'family',
                label: 'Tipo',
                placeholder: 'Todos',
                options: (
                  ['recorrencia', 'direta', 'antecipada', 'produto'] as const
                ).map((f) => ({
                  value: f,
                  label: familyLabel(f),
                })),
              },
              {
                type: 'selecao',
                name: 'basis',
                label: 'Base',
                placeholder: 'Todas',
                options: [
                  { value: 'proprio', label: 'Carteira própria' },
                  { value: 'hierarquia', label: 'De líder (liderados diretos)' },
                ],
              },
              {
                type: 'selecao',
                name: 'linked',
                label: 'Vinculação',
                placeholder: 'Todos',
                options: [
                  { value: 'com', label: 'Com vinculados' },
                  { value: 'sem', label: 'Sem ninguém vinculado' },
                ],
              },
              {
                type: 'selecao',
                name: 'situation',
                label: 'Situação',
                required: true,
                help: 'Vazio mostra só os ativos.',
                options: [
                  { value: 'ativos', label: 'Só os ativos' },
                  { value: 'todos', label: 'Ativos e arquivados' },
                  { value: 'arquivados', label: 'Só os arquivados' },
                ],
              },
            ]}
          />
        }
      />

      {templates.data !== undefined && templates.data.length === 0 && (
        <EmptyState icon="documento" title="Nenhum modelo nesta base.">
          Cadastre a régua uma vez em “Novo modelo” e vincule aos consultores — em vez de
          digitar a mesma premiação na ficha de cada um.
        </EmptyState>
      )}

      {/*
       * Um lote de cada lado desde 15/10/2026, a pedido do responsável: numa
       * coluna só, os modelos 1x e 2x se misturavam e o lote era um selo
       * pequeno no meio do cartão. A categoria é o lote de pagamento, e é por
       * ele que quem cadastra procura o modelo. Cada tabela pagina de 5 em 5 e
       * ordena pelo cabeçalho; a busca e os filtros acima valem para as duas.
       */}
      {(templates.isPending || (templates.data?.length ?? 0) > 0) &&
        (['1x', '2x'] as const).map((category) => {
          const ofCategory = shown.filter((t) => t.category === category);
          return (
            <section
              key={category}
              aria-labelledby={`modelos-lote-${category}`}
              className="flex flex-col gap-3"
            >
              <h2
                id={`modelos-lote-${category}`}
                className="text-base font-semibold text-texto"
              >
                Lote {category}{' '}
                <span className="text-sm font-normal text-texto-suave">
                  · {ofCategory.length} modelo(s)
                </span>
              </h2>
              <Table
                label={`Modelos de premiação do lote ${category}`}
                columns={columns}
                items={ofCategory}
                loading={templates.isPending}
                rowKey={(t) => String(t.id)}
                rowTitle={(t) => t.title}
                rowActions={actionsOf}
                expand={(t) => (
                  <LinkedConsultants
                    template={t}
                    busy={busy}
                    unlinking={unlinking}
                    onUnlink={(linked) => unlink.mutate(linked)}
                    onRemove={(linked) => remove.mutate(linked)}
                  />
                )}
                empty={
                  search !== '' || hasFilter(filters)
                    ? `Nenhum modelo do lote ${category} com esta busca e estes filtros.`
                    : `Nenhum modelo no lote ${category}. Ao cadastrar, escolha a categoria ${category}.`
                }
                framed
                defaultPerPage={5}
              />
            </section>
          );
        })}
    </Page>
  );
}

type Situation = 'ativos' | 'todos' | 'arquivados';

/** Todas as chaves dos filtros, vazias — o "Limpar" aplica isto (ver `Filters`). */
const NO_FILTER: FilterValues = { family: '', basis: '', linked: '', situation: '' };

function hasFilter(filters: FilterValues): boolean {
  return (
    ['family', 'basis', 'linked'].some((k) => String(filters[k] ?? '') !== '') ||
    String(filters.situation || 'ativos') !== 'ativos'
  );
}

/** O modelo passa na busca e no recorte? A busca olha o título e quem está vinculado. */
function matches(
  t: AwardTemplate,
  search: string,
  filters: FilterValues,
  situation: Situation,
): boolean {
  const term = search.trim().toLocaleLowerCase('pt-BR');
  if (
    term !== '' &&
    !t.title.toLocaleLowerCase('pt-BR').includes(term) &&
    !t.consultants.some((c) => c.user.nome.toLocaleLowerCase('pt-BR').includes(term))
  ) {
    return false;
  }
  const family = String(filters.family ?? '');
  if (family !== '' && t.family !== family) return false;
  const basis = String(filters.basis ?? '');
  if (basis !== '' && t.basisSource !== basis) return false;
  const linked = String(filters.linked ?? '');
  if (linked === 'com' && t.consultants.length === 0) return false;
  if (linked === 'sem' && t.consultants.length > 0) return false;
  /* "ativos" já veio filtrado do servidor; "arquivados" recorta aqui */
  if (situation === 'arquivados' && t.archivedAt === null) return false;
  return true;
}

/** A régua resumida, numa frase. */
function ruleOf(template: AwardTemplate): ReactNode {
  if (template.family === 'recorrencia') {
    return (
      <>
        {template.percentage === null ? '—' : `${formatPercentage(template.percentage)}%`}{' '}
        sobre os boletos pagos
        {template.basisSource === 'hierarquia'
          ? ' dos liderados diretos'
          : ' da carteira'}
      </>
    );
  }
  if (template.family === 'produto') {
    return template.products.length === 0 ? (
      <span className="text-alerta">sem produto — as premiações dele não apuram</span>
    ) : (
      <>
        {template.products.length} produto(s), por veículo com boleto pago
        {template.productCombination === 'maior'
          ? ' — paga o maior'
          : ' — soma os produtos'}
      </>
    );
  }
  return template.tiers.length === 0 ? (
    <span className="text-alerta">sem faixa — as premiações dele não apuram</span>
  ) : (
    <>
      <MoneyValue value={template.tiers[0]!.amount} />
      {template.tiers.length > 1 && (
        <>
          {' a '}
          <MoneyValue value={template.tiers[template.tiers.length - 1]!.amount} />
        </>
      )}{' '}
      por faixa de placas ativadas
    </>
  );
}

/**
 * Quem está com o modelo — o painel que abre ao expandir a linha.
 *
 * Desvincular arquiva; apagar some de vez e pede o segundo clique — no próprio
 * item, e não num diálogo: a pessoa está olhando para o nome de quem perde a
 * premiação.
 */
function LinkedConsultants({
  template,
  busy,
  unlinking,
  onUnlink,
  onRemove,
}: {
  template: AwardTemplate;
  busy: boolean;
  /** A premiação sendo arquivada ou apagada agora, para o botão dela girar. */
  unlinking: number | null;
  onUnlink: (linked: AwardTemplate['consultants'][number]) => void;
  onRemove: (linked: AwardTemplate['consultants'][number]) => void;
}) {
  const [confirming, setConfirming] = useState<number | null>(null);

  if (template.consultants.length === 0) {
    return <span className="text-sm text-texto-suave">Ninguém vinculado ainda.</span>;
  }

  return (
    <ul className="flex flex-wrap gap-2">
      {template.consultants.map((c) => (
        <li
          key={c.awardId}
          className="flex items-center gap-2 rounded-full border border-borda py-0.5 pl-3 pr-1 text-xs text-texto"
        >
          {c.user.nome}
          {confirming === c.awardId ? (
            <>
              <span className="text-erro">Apagar de vez?</span>
              <Button
                variant="perigo"
                size="sm"
                loading={unlinking === c.awardId}
                onClick={() => {
                  setConfirming(null);
                  onRemove(c);
                }}
              >
                Sim, apagar
              </Button>
              <Button variant="discreto" size="sm" onClick={() => setConfirming(null)}>
                Não
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="discreto"
                size="sm"
                disabled={busy || (unlinking !== null && unlinking !== c.awardId)}
                loading={unlinking === c.awardId}
                onClick={() => onUnlink(c)}
              >
                Desvincular
              </Button>
              <Button
                variant="discreto"
                size="sm"
                disabled={busy || unlinking !== null}
                onClick={() => setConfirming(c.awardId)}
              >
                Apagar
              </Button>
            </>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * Escolher a quem vincular. Quem já tem uma premiação ativa deste modelo não
 * aparece na lista — o servidor recusaria, e a opção seria um botão que abre
 * uma recusa.
 */
function LinkPanel({
  template,
  onClose,
  onLinked,
}: {
  template: AwardTemplate;
  onClose: () => void;
  onLinked: (count: number) => void;
}) {
  const consultants = useConsultants();
  const [chosen, setChosen] = useState<string[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const linked = new Set(template.consultants.map((c) => c.id));

  const options = (consultants.data ?? [])
    .filter((c) => c.archivedAt === null && !linked.has(c.id))
    .map((c) => ({
      value: String(c.id),
      label: `${c.user.nome} · código ${c.erpVolunteerCode}`,
    }));

  const apply = useMutation({
    mutationFn: async () => {
      const r = await api.sales.applyAwardTemplate({
        params: { id: template.id },
        body: { consultantIds: chosen.map(Number) },
      });
      if (r.status !== 201) throw new Error(refusalOf(r, 'o vínculo'));
      return r.body.length;
    },
    onSuccess: onLinked,
    onError: (e: Error) => setFailure(e.message),
  });

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title={`Vincular “${template.title}”`}
      descricao="Cada consultor escolhido ganha uma premiação própria com esta régua, que pode ser editada depois só para ele."
    >
      <div className="flex flex-col gap-4">
        {failure !== null && <Alert tone="erro">{failure}</Alert>}

        <MultiSelect
          label="Consultores"
          placeholder="Escolha…"
          searchPlaceholder="Buscar consultor…"
          options={options}
          selected={chosen}
          onChange={setChosen}
          loading={consultants.isPending}
          /*
           * `modal`: a lista abre acima do véu do diálogo. Sem isto ela abria em
           * `z-10`, por baixo do véu, e não recebia clique — o "Vincular" ficou
           * quebrado quando passou a abrir em modal, em 15/10/2026.
           */
          layer="modal"
          emptyMessage="Nenhum consultor disponível — quem já tem este modelo não aparece."
        />

        <ModalActions>
          <Button variant="contorno" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            disabled={chosen.length === 0}
            loading={apply.isPending}
            onClick={() => {
              setFailure(null);
              apply.mutate();
            }}
          >
            {chosen.length <= 1 ? 'Vincular' : `Vincular ${chosen.length}`}
          </Button>
        </ModalActions>
      </div>
    </Modal>
  );
}
